import { Button, Input, Radio, Space } from "antd";
import { DetailPageErrorState } from "@/views/components/DetailPageErrorState";
import type { BizOperationHistoryVo, CarrierShip, PlatClueVo } from "@zhihaoscm/entity";
import { formatNumber } from "@zhihaoscm/utils";
import { BIZ_OPERATION_HISTORY_DEF, TRANSPORT_CAPACITY_E_TYPE } from "@zhihaoscm/meta";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";
import { DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { QUOTE_IDENTITY } from "@/meta/logistics/shipNeed";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import { getValueByPath } from "@/utils/object";
import { formatAmountData } from "@/views/pages/purchasingManage";

interface BaseInfoConfig {
    label: string;
    prop: string;
    formatter?: (value: unknown, data: PlatClueVo) => string | number;
}

/** 读取旧 Vue 基本信息配置的嵌套字段，并保留统一的缺省值。 */
function getBaseInfoValue(item: BaseInfoConfig, data: PlatClueVo) {
    const value = getValueByPath(data as unknown as Record<string, unknown>, item.prop);
    return item.formatter ? item.formatter(value, data) : (value ?? DEFAULT_VALUE);
}

/** 注册用户基本信息，字段和 prop 与旧 Vue baseInfo 保持一致。 */
const userBasicInfo: BaseInfoConfig[] = [
    { label: "注册用户ID", prop: "customer.id" },
    {
        label: "个人实名/组织机构",
        prop: "customer.institutionName",
        formatter: (_value, data) => data.customer?.institutionName || data.customer?.realName || DEFAULT_VALUE
    },
    { label: "注册手机号", prop: "customer.mobile" },
    {
        label: "注册时间",
        prop: "customer.createdTime",
        formatter: value => dateFormat(value as string, DATETIME_PICKER_FORMAT)
    }
];

/** 找船需求基本信息，沿用 Vue 的装卸日期和天数展示口径。 */
const demandBasicInfo: BaseInfoConfig[] = [
    { label: "需求编号", prop: "shippingRequirementPlat.id" },
    {
        label: "货主",
        prop: "shippingRequirementPlat.ownerEnterprise.realName",
        formatter: (_value, data) =>
            data.shippingRequirementPlat?.ownerEnterprise?.name ||
            data.shippingRequirementPlat?.ownerEnterprise?.realName ||
            DEFAULT_VALUE
    },
    { label: "运货吨数", prop: "shippingRequirementPlat.freightTons" },
    { label: "始发地", prop: "shippingRequirementPlat.sourcePortName" },
    { label: "目的地", prop: "shippingRequirementPlat.destinationPortName" },
    {
        label: "预期装卸日期",
        prop: "shippingRequirementPlat.loadDate",
        formatter: (value, data) =>
            `${dateFormat(value as string)} +${data.shippingRequirementPlat?.loadDays ?? DEFAULT_VALUE}天`
    },
    { label: "装卸天数", prop: "shippingRequirementPlat.loadUnloadDays" },
    {
        label: "发布时间",
        prop: "shippingRequirementPlat.createdTime",
        formatter: value => dateFormat(value as string, DATETIME_PICKER_FORMAT)
    }
];

/** 报价基本信息，按船主/船代身份切换承运商、联系人和电话。 */
const quoteBasicInfo: BaseInfoConfig[] = [
    { label: "报价需求编号", prop: "shippingRequirementAccept.requirementPlatId" },
    {
        label: "承运商",
        prop: "shippingRequirementAccept.captainEnterprise.realName",
        formatter: (_value, data) => {
            const accept = data.shippingRequirementAccept;
            if (QUOTE_IDENTITY.SHIP_OWNER.match(accept?.quoteIdentity)) {
                return accept.captainEnterprise?.name || accept.captainEnterprise?.realName || DEFAULT_VALUE;
            }
            if (QUOTE_IDENTITY.SHIP_AGENT.match(accept?.quoteIdentity)) {
                return accept.agentEnterprise?.name || accept.agentEnterprise?.realName || DEFAULT_VALUE;
            }
            return DEFAULT_VALUE;
        }
    },
    {
        label: "联系人",
        prop: "shippingRequirementAccept.quoteIdentity",
        formatter: (_value, data) => {
            const accept = data.shippingRequirementAccept;
            return QUOTE_IDENTITY.SHIP_AGENT.match(accept?.quoteIdentity)
                ? accept.agentContact || DEFAULT_VALUE
                : accept?.captainEnterprise?.realName || DEFAULT_VALUE;
        }
    },
    {
        label: "联系电话",
        prop: "shippingRequirementAccept.quoteIdentity",
        formatter: (_value, data) => {
            const accept = data.shippingRequirementAccept;
            return QUOTE_IDENTITY.SHIP_AGENT.match(accept?.quoteIdentity)
                ? accept.agentMobile || DEFAULT_VALUE
                : accept?.captainEnterprise?.mobile || DEFAULT_VALUE;
        }
    },
    {
        label: "报价单价",
        prop: "shippingRequirementAccept.shipInfos",
        formatter: (_value, data) => {
            const ship = data.shippingRequirementAccept?.shipInfos?.[0] as CarrierShip | undefined;
            return ship?.unitPrice ? `${formatNumber(ship.unitPrice, 2)}元/吨` : DEFAULT_VALUE;
        }
    },
    {
        label: "装货吨数",
        prop: "shippingRequirementAccept.shipInfos",
        formatter: (_value, data) => {
            const ship = data.shippingRequirementAccept?.shipInfos?.[0] as CarrierShip | undefined;
            return ship?.ton ? formatNumber(ship.ton, 0) : DEFAULT_VALUE;
        }
    },
    {
        label: "预估运费",
        prop: "shippingRequirementAccept.shipInfos",
        formatter: (_value, data) => {
            const ship = data.shippingRequirementAccept?.shipInfos?.[0] as CarrierShip | undefined;
            return ship?.unitPrice && ship.ton
                ? `${formatAmountData(Number(ship.unitPrice * ship.ton).toFixed(2))}元`
                : DEFAULT_VALUE;
        }
    },
    {
        label: "报价时间",
        prop: "shippingRequirementAccept.acceptTime",
        formatter: value => dateFormat(value as string, DATETIME_PICKER_FORMAT)
    }
];

/** 空船基本信息，保留港口列表和空船时间范围的旧 Vue 展示规则。 */
const emptyShipBasicInfo: BaseInfoConfig[] = [
    { label: "空船信息编号", prop: "emptyShipVo.emptyShip.id" },
    {
        label: "承运商",
        prop: "emptyShipVo.carrier.realName",
        formatter: (_value, data) =>
            data.emptyShipVo?.carrier?.institutionName || data.emptyShipVo?.carrier?.realName || DEFAULT_VALUE
    },
    { label: "实装吨数", prop: "emptyShipVo.emptyShip.tonCapacity" },
    {
        label: "空船始发地",
        prop: "emptyShipVo.originPorts",
        formatter: (_value, data) =>
            data.emptyShipVo?.originPorts?.map(item => item.portName).join("、") || DEFAULT_VALUE
    },
    {
        label: "目的地",
        prop: "emptyShipVo.destPorts",
        formatter: (_value, data) => data.emptyShipVo?.destPorts?.map(item => item.portName).join("、") || DEFAULT_VALUE
    },
    {
        label: "预期空船时间",
        prop: "emptyShipVo.emptyShip",
        formatter: (_value, data) => {
            const emptyShip = data.emptyShipVo?.emptyShip;
            return `${dateFormat(emptyShip?.emptyStartTime, DATE_PICKER_FORMAT)} 至 ${dateFormat(
                emptyShip?.emptyEndTime,
                DATE_PICKER_FORMAT
            )}`;
        }
    },
    { label: "发布人", prop: "emptyShipVo.emptyShip.publisherName" },
    {
        label: "发布时间",
        prop: "emptyShipVo.emptyShip.publishTime",
        formatter: value => dateFormat(value as string, DATETIME_PICKER_FORMAT)
    }
];

/** 按详情 query tab 选择唯一一套旧 Vue 基本信息字段。 */
const basicInfoByTab: Partial<Record<TRANSPORT_CAPACITY_E_TYPE, BaseInfoConfig[]>> = {
    [TRANSPORT_CAPACITY_E_TYPE.NEW_USER]: userBasicInfo,
    [TRANSPORT_CAPACITY_E_TYPE.NEW_SHIPPING_REQUIREMENT_PLAT]: demandBasicInfo,
    [TRANSPORT_CAPACITY_E_TYPE.NEW_SHIPPING_REQUIREMENT_ACCEPT]: quoteBasicInfo,
    [TRANSPORT_CAPACITY_E_TYPE.NEW_EMPTY_SHIP]: emptyShipBasicInfo
};

export async function loadPlatformClue(id: number | string) {
    const { platClueService } = await import("@/api/services/platClueService");
    return platClueService.getPlatClueInfo(id);
}
export async function updatePlatformClue(id: number | string, form: { state: number; remark: string }) {
    const { platClueService } = await import("@/api/services/platClueService");
    return platClueService.platClueDeal(id, { ...form, handlerId: 0 });
}
export async function loadPlatformClueRecords(id: number | string) {
    const { bizOperationHistoryService } = await import("@/api/services/bizOperationHistoryService");
    return bizOperationHistoryService.getBizOperationHistorySelect({
        operationModule: BIZ_OPERATION_HISTORY_DEF.CLUE_PROCESSING_RECORDING_HANDLE.code,
        bizNo: String(id)
    });
}
export interface PlatformCluesDetailPageProps {
    loadClue?: (id: number | string) => Promise<PlatClueVo>;
    loadProcessRecords?: (id: number | string) => Promise<BizOperationHistoryVo[]>;
    dealClue?: (id: number | string, form: { state: number; remark: string }) => Promise<unknown>;
    permissions?: string[];
}

/** 平台线索详情：保留 Vue 的详情接口、状态及处理权限。 */
export function PlatformCluesDetailPage({
    loadClue = loadPlatformClue,
    loadProcessRecords = loadPlatformClueRecords,
    dealClue = updatePlatformClue,
    permissions
}: PlatformCluesDetailPageProps) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const canWrite = (permissions || storePermissions).includes("ROLE_PLATFORM_CLUE_W");
    const [data, setData] = useState<PlatClueVo | null>(null);
    const [processRecords, setProcessRecords] = useState<BizOperationHistoryVo[]>([]);
    const [error, setError] = useState("");
    const [processing, setProcessing] = useState(false);
    const [remark, setRemark] = useState("");
    const [processState, setProcessState] = useState(2);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!id) return;
        void loadClue(id)
            .then(setData)
            .catch(() => setError("平台线索详情加载失败"));
        // 处理记录独立于详情主体失败，避免历史接口异常导致详情页白屏。
        void loadProcessRecords(id)
            .then(setProcessRecords)
            .catch(() => setProcessRecords([]));
    }, [id, loadClue, loadProcessRecords]);
    if (error)
        return (
            <DetailPageErrorState
                ariaLabel="platform-clues-detail-page"
                message={error}
                onBack={() => navigate(-1)}
            />
        );
    if (!data) return <section aria-label="platform-clues-detail-page" aria-busy="true" />;
    const clue = data.platClue;
    const tab = Number(search.get("tab")) as TRANSPORT_CAPACITY_E_TYPE;
    const basicInfo = basicInfoByTab[tab] || [];
    const title =
        (
            { 1: "注册用户信息详情", 2: "找船需求信息详情", 3: "报价信息详情", 4: "空船信息详情" } as Record<
                string,
                string
            >
        )[search.get("tab") || ""] || "平台线索详情";
    const stateDesc =
        ({ 0: "未处理", 1: "持续跟进", 2: "已处理" } as Record<number, string>)[Number(clue.state)] || "未知";
    /** 提交平台线索处理结果，保持 Vue 处理成功后刷新详情的状态流转。 */
    const submitProcessing = async () => {
        if (saving || !id) return;
        setSaving(true);
        try {
            await dealClue(id, { state: processState, remark });
            setProcessing(false);
            const refreshed = await loadClue(id);
            setData(refreshed);
        } catch {
            setError("平台线索处理失败");
        } finally {
            setSaving(false);
        }
    };
    return (
        <section aria-label="platform-clues-detail-page" className="flex min-h-full flex-col">
            <div className="section-card">
                <ZhDetailHeader
                    leftInfos={[{ label: "线索编号", value: clue.id }]}
                    stateDesc={stateDesc}
                    title={title}
                />
                <div className="section-title mb-4 mt-6">基本信息</div>
                <ZhMultiGrid>
                    <ZhGrid cols={4} gap={[20, 20]}>
                        {basicInfo.map(item => (
                            <ZhBaseItem
                                key={`${item.label}-${item.prop}`}
                                label={item.label}
                                value={getBaseInfoValue(item, data)}
                            />
                        ))}
                    </ZhGrid>
                </ZhMultiGrid>
                <h3>处理记录</h3>
                <div aria-label="平台线索处理记录">
                    {processRecords.length ? (
                        processRecords.map((record, index) => (
                            <div key={`${record.createdTime}-${index}`}>
                                <div>
                                    {dateFormat(record.createdTime, DATETIME_PICKER_FORMAT)} 处理人：
                                    {record.operatorName || "--"}
                                </div>
                                <div>
                                    {(record.operationContent || []).map((item, itemIndex) => (
                                        <span key={`${String(item.key)}-${itemIndex}`}>
                                            {String(item.key)}：{String(item.value ?? "--")}{" "}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : clue.handleInfo?.length ? (
                        clue.handleInfo.map((item, index) => (
                            <div key={`${item.handleTime}-${index}`}>
                                {item.handlerName || "--"}：{item.remark || "--"}
                            </div>
                        ))
                    ) : (
                        <div>暂无处理记录</div>
                    )}
                </div>
                <Space className="mt-4">
                    {canWrite && [0, 1].includes(Number(clue.state)) ? (
                        <Button
                            type="primary"
                            onClick={() => {
                                setProcessState(2);
                                setProcessing(true);
                            }}
                        >
                            处理
                        </Button>
                    ) : null}
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
                <ZhDialog
                    open={processing}
                    title="处理平台线索"
                    width={400}
                    appendToBody
                    footer={
                        <Space>
                            <Button disabled={saving} onClick={() => setProcessing(false)}>
                                取消
                            </Button>
                            <Button loading={saving} type="primary" onClick={() => void submitProcessing()}>
                                确认处理
                            </Button>
                        </Space>
                    }
                    onCancel={() => {
                        if (!saving) setProcessing(false);
                    }}
                >
                    <ZhGrid cols={1} gap={[0, 0]}>
                        <ZhGridItem label="是否处理">
                            <Radio.Group
                                aria-label="是否处理"
                                value={processState}
                                options={[1, 2].map(value => ({
                                    value,
                                    label: value === 1 ? "持续跟进" : "已处理"
                                }))}
                                onChange={event => setProcessState(event.target.value)}
                            />
                        </ZhGridItem>
                        <ZhGridItem label="处理说明">
                            <Input.TextArea
                                aria-label="处理备注"
                                value={remark}
                                onChange={event => setRemark(event.target.value)}
                            />
                        </ZhGridItem>
                    </ZhGrid>
                </ZhDialog>
            </div>
        </section>
    );
}

export default PlatformCluesDetailPage;
